import type { Dispatch, RefObject, SetStateAction } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { CdropdownOption } from "./Cdropdown.types";

interface UseDropdownOptionsHeightMeasurementInput {
  isOpen: boolean;
  options: CdropdownOption[];
  maxHeight: number | undefined;
  minHeight: number | undefined;
}

interface UseDropdownOptionsHeightMeasurementResult {
  optionsContentRef: RefObject<HTMLDivElement | null>;
  optionsViewportHeight: number | null;
}

interface DropdownOptionsHeightMeasurementLifecycleInput {
  isOpen: boolean;
  options: CdropdownOption[];
  optionsContentRef: RefObject<HTMLDivElement | null>;
  setOptionsViewportHeight: Dispatch<SetStateAction<number | null>>;
  measureOptionsViewportHeight: () => void;
}

interface DropdownOptionsHeightMeasurementEffectInput {
  isOpen: boolean;
  measureOptionsViewportHeight: () => void;
}

interface DropdownOptionsResizeObserverInput {
  isOpen: boolean;
  optionsContentRef: RefObject<HTMLDivElement | null>;
  measureOptionsViewportHeight: () => void;
}

interface DropdownOptionsHeightResetInput {
  isOpen: boolean;
  setOptionsViewportHeight: Dispatch<SetStateAction<number | null>>;
}

function resolveMaxHeightPx(maxHeight: number | undefined): number {
  return maxHeight ?? Number.POSITIVE_INFINITY;
}

function resolveMeasuredOptionsViewportHeight(input: {
  isOpen: boolean;
  optionsContentElement: HTMLDivElement | null;
  maxHeight: number | undefined;
  minHeight: number | undefined;
}): number | null {
  if (!input.isOpen) {
    return null;
  }

  if (!input.optionsContentElement) {
    return null;
  }

  const contentHeight = input.optionsContentElement.scrollHeight;
  const minHeightPx = input.minHeight ?? 0;
  const maxHeightPx = resolveMaxHeightPx(input.maxHeight);
  return Math.min(maxHeightPx, Math.max(contentHeight, minHeightPx));
}

function useDropdownOptionsMeasurementFrame({
  isOpen,
  options,
  measureOptionsViewportHeight,
}: DropdownOptionsHeightMeasurementEffectInput & {
  options: CdropdownOption[];
}): void {
  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const measurementFrame = window.requestAnimationFrame(() => {
      measureOptionsViewportHeight();
    });

    return () => {
      window.cancelAnimationFrame(measurementFrame);
    };
  }, [isOpen, measureOptionsViewportHeight, options]);
}

function useDropdownOptionsResizeObserver({
  isOpen,
  optionsContentRef,
  measureOptionsViewportHeight,
}: DropdownOptionsResizeObserverInput): void {
  useEffect(() => {
    if (!isOpen || typeof ResizeObserver === "undefined") {
      return;
    }

    const optionsContentElement = optionsContentRef.current;
    if (!optionsContentElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      measureOptionsViewportHeight();
    });

    resizeObserver.observe(optionsContentElement);
    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, measureOptionsViewportHeight, optionsContentRef]);
}

function useDropdownOptionsHeightReset({
  isOpen,
  setOptionsViewportHeight,
}: DropdownOptionsHeightResetInput): void {
  useEffect(() => {
    if (isOpen) {
      return;
    }

    const resetFrame = window.requestAnimationFrame(() => {
      setOptionsViewportHeight(null);
    });

    return () => {
      window.cancelAnimationFrame(resetFrame);
    };
  }, [isOpen, setOptionsViewportHeight]);
}

function useDropdownOptionsHeightMeasurementLifecycle({
  isOpen,
  options,
  optionsContentRef,
  setOptionsViewportHeight,
  measureOptionsViewportHeight,
}: DropdownOptionsHeightMeasurementLifecycleInput): void {
  useDropdownOptionsMeasurementFrame({
    isOpen,
    options,
    measureOptionsViewportHeight,
  });
  useDropdownOptionsResizeObserver({
    isOpen,
    optionsContentRef,
    measureOptionsViewportHeight,
  });
  useDropdownOptionsHeightReset({ isOpen, setOptionsViewportHeight });
}

export function useDropdownOptionsHeightMeasurement({
  isOpen,
  options,
  maxHeight,
  minHeight,
}: UseDropdownOptionsHeightMeasurementInput): UseDropdownOptionsHeightMeasurementResult {
  const optionsContentRef = useRef<HTMLDivElement>(null);
  const [optionsViewportHeight, setOptionsViewportHeight] = useState<
    number | null
  >(null);

  const measureOptionsViewportHeight = useCallback(() => {
    const clampedHeight = resolveMeasuredOptionsViewportHeight({
      isOpen,
      optionsContentElement: optionsContentRef.current,
      maxHeight,
      minHeight,
    });

    if (clampedHeight === null) {
      return;
    }

    setOptionsViewportHeight((previousHeight) =>
      previousHeight === clampedHeight ? previousHeight : clampedHeight,
    );
  }, [isOpen, maxHeight, minHeight]);

  useDropdownOptionsHeightMeasurementLifecycle({
    isOpen,
    options,
    optionsContentRef,
    setOptionsViewportHeight,
    measureOptionsViewportHeight,
  });

  return {
    optionsContentRef,
    optionsViewportHeight,
  };
}
